'use client';

import { useEffect, useRef } from 'react';

type ThirdPartyEvent<Detail extends EventDetail> = CustomEvent<Detail>;

type ThirdPartyEventListener<Detail extends EventDetail> = (
  ev: ThirdPartyEvent<Detail>,
) => void;

type EventDetail = Record<string, any>;

type EventName = string;

export type UnboundThirdPartyEventInvoker = (name: EventName) => void;
export type BoundThirdPartyEventInvoker<Detail> = (detail?: Detail) => void;
export type BoundThirdPartyEventRegisterer<
  Detail extends EventDetail = EventDetail,
> = (callback: ThirdPartyEventListener<Detail>) => void;

type EventControllerUnbound = {
  invoke: UnboundThirdPartyEventInvoker;
};

type EventControllerBound<Detail extends EventDetail> = {
  invoke: BoundThirdPartyEventInvoker<Detail>;
  listen: BoundThirdPartyEventRegisterer<Detail>;
  unlisten: () => void;
};

const createPrefixedName = (name: EventName) => `tuic:custom:${name}`;

const dispatchEvent = (name: EventName, detail?: EventDetail) => {
  if (!window) {
    return;
  }

  window.dispatchEvent(new CustomEvent(name, detail ? { detail } : undefined));
};

/**
 * Invoke & listen to a specific third party event across components.
 *
 * See the following example to get a picture of a use case for this hook.
 *
 * ```tsx
 * 'use client';
 *
 * import { useState } from 'react';
 * import { useThirdPartyEvent } from @tuic/lib;
 * import { fetchItemsFromCms } from './path/to/cms-fetcher.ts';
 *
 * // Aim for specifying event names only once to ease potential later refactoring endeavours.
 * const EVENT_OPEN_COOKIE_SETTINGS = 'openCookieSettings';
 *
 * // This would be some sort of navigation, where the exact definition for the "open cookie settings"
 * // navigation items comes from a cms.
 * const Navigation = () => {
 *   // Use the hook to get a function which you can use to invoke the event
 *   const { invoke } = useThirdPartyEvent(EVENT_OPEN_COOKIE_SETTINGS);
 *
 *   // Let's imagine we're fetching some items here.
 *   const items = fetchItemsFromCms();
 *
 *   const handleClick = (ev, item) => {
 *     // We only want to handle the related "open cookie settings" item.
 *     if (!item.href === '#openCookieSettings') {
 *       return;
 *     }
 *
 *     // Let's prevent the defaul action ...
 *     ev.preventDefault();
 *
 *     // ... and invoke the third party event.
 *     invoke();
 *   }
 *
 *   // Just a simple navigation list -- nothing fancy here.
 *   return (
 *     <ul>
 *       {items.map(item => (
 *         <li key={item.id}>
 *           <a href={item.href} onClick={ev => handleClick(ev, item)}>
 *             {item.title}
 *           </a>
 *         </li>
 *       ))}
 *     </ul>
 *   );
 * };
 *
 * // Your cookie settings dialog would usually live somewhere completely unrelated to the navigation.
 * // At this point you should slowly get why this hook exists.
 * // It's here to avoid wrapping your whole app in a client component and leaving RSC features behind.
 * const CookieSettings = () => {
 *   const [showCookieSettings, setShowCookieSettings] = useState(false);
 *
 *   // Let's get the listener register function for our event.
 *   const { listen } = useThirdPartyEvent(EVENT_OPEN_COOKIE_SETTINGS);
 *
 *   // Register a listener to our event.
 *   // You don't need to worry to remove your listeners.
 *   // They're automatically cleaned up once your component unmounts.
 *   listen(() => {
 *
 *     // When the event is invoked from above Navigation component,
 *     // this functions gets executed and sets the `showCookieSettings` variable to true.
 *     setShowCookieSettings(true);
 *   });
 *
 *   // Now we only need to render a cookie settings dialog.
 *   if (!showCookieSettings) {
 *     return null;
 *   }
 *
 *   return (
 *     <div>
 *      // ... render some sort of cookie settings dialog.
 *    </div>
 *   );
 * }
 * ```
 */
function useThirdPartyEvent<Detail extends EventDetail = EventDetail>(
  name: EventName,
): EventControllerBound<Detail>;

/**
 * Invoke any third party events.
 *
 * See the following example to get a picture of a use case for this hook.
 *
 * ```tsx
 * 'use client';
 *
 * import { useThirdPartyEvent } from @tuic/lib;
 * import { fetchItemsFromCms } from './path/to/cms-fetcher.ts';
 *
 * // This would be some sort of navigation, where the event names are defined in the CMS.
 * const Navigation = () => {
 *   // Use the hook to get a function which you can use to invoke third party events.
 *   const { invoke } = useThirdPartyEvent();
 *
 *   // Let's imagine we're fetching some items here.
 *   const items = fetchItemsFromCms();
 *
 *   const handleClick = (ev, item) => {
 *     invoke(item.eventName);
 *   }
 *
 *   return (
 *     <ul>
 *       {items.map(item => (
 *         <li key={item.id}>
 *           <a href={item.href} onClick={ev => handleClick(ev, item)}>
 *             {item.title}
 *           </a>
 *         </li>
 *       ))}
 *     </ul>
 *   );
 * };
 */
function useThirdPartyEvent(): EventControllerUnbound;

function useThirdPartyEvent<Detail extends EventDetail = EventDetail>(
  name?: EventName,
) {
  const handle = useRef<ThirdPartyEventListener<Detail> | undefined>(undefined);
  const handlerRegistered = useRef(false);
  const stashedEvents = useRef<Detail[]>([]);

  const win = typeof window === 'undefined' ? null : window;

  const prefixedName = name ? createPrefixedName(name) : undefined;

  useEffect(() => {
    if (!win || !handle.current || !prefixedName) {
      return;
    }

    win.addEventListener(prefixedName, handle.current as EventListener);

    handlerRegistered.current = true;
    stashedEvents.current.forEach(event => {
      dispatchEvent(prefixedName, event);
    });
    stashedEvents.current = [];

    return () => {
      win.removeEventListener(prefixedName, handle.current as EventListener);
    };
  }, [win ?? undefined, handle.current, prefixedName]);

  if (!prefixedName) {
    return {
      invoke: (name: EventName, detail?: Detail) => {
        dispatchEvent(createPrefixedName(name), detail);
      },
    };
  }

  return {
    invoke: (detail?: Detail) => {
      if (detail && !handlerRegistered.current) {
        stashedEvents.current.push(detail);
        return;
      }
      dispatchEvent(prefixedName, detail);
    },
    listen: (callback: ThirdPartyEventListener<Detail>) => {
      handle.current = callback;
    },
    unlisten: () => {
      win?.removeEventListener(prefixedName, handle.current as EventListener);
      handle.current = undefined;
      handlerRegistered.current = false;
    },
  };
}

export { useThirdPartyEvent };
